/**
 * Build a lightweight market_context for POST /api/ai/chat.
 * Full scanner tables are kept in React state for UI; only a capped sample is sent
 * so requests stay fast and do not time out / disconnect.
 */

export const CHAT_PAYLOAD_MAX_ROWS = 100;
export const CHAT_SYMBOL_FILTER_MAX_ROWS = 200;

const TICKER_PATTERN =
    /\b(SPXW\.X|SPX\.X|NDX\.X|RUT\.X|VIX\.X|SPY|QQQ|TSLA|PLTR|META|MSFT|NVDA|AAPL|AMZN|AMD|INTC|GLD|SPX|NDX|MU|IWM|TLT|SLV|IBIT|XLE|XLF|EWZ|NFLX|FXI|SOFI|EEM|NIO|PFE|OPEN|MSTR|BABA|CORZ|GRAB|SMCI|PYPL|HYG|IREN|EOSE|SNAP|MARA|GME|RIVN|WULF|ORCL|BAC|HOOD|ETHA|BMNR|GOOG|JD|AVGO|KRE|F|VALE|EFA|TSM|IEF|C|AAL|LQD|GDX|XLU|KWEB|UNH|COIN|ASST)\b/gi;

function extractMentionedSymbols(message: string): string[] {
    const found = new Set<string>();
    const matches = message.match(TICKER_PATTERN);
    if (matches) {
        for (const sym of matches) {
            if (sym) found.add(sym.toUpperCase());
        }
    }
    return Array.from(found);
}

function rowSymbol(row: Record<string, unknown>): string {
    const u = String(row?.Underlying ?? row?.Symbol ?? "").toUpperCase();
    if (!u) return "";
    return u.split(/\s/)[0];
}

function rowMatchesSymbol(
    row: Record<string, unknown>,
    sym: string,
): boolean {
    const u = rowSymbol(row);
    const s = sym.toUpperCase();
    if (!u || !s) return false;
    const uBase = u.split(".")[0];
    const sBase = s.split(".")[0];
    return u.includes(s) || s.includes(u) || uBase === sBase;
}

/** Strip heavy fields and cap rawData before sending to /api/ai/chat. */
export function buildChatPostMarketContext(
    marketContext: Record<string, unknown> | undefined,
    userMessage: string,
): Record<string, unknown> {
    if (!marketContext || typeof marketContext !== "object") return {};

    const { rawData, filtered_data, real_data_sample, all_data, ...rest } =
        marketContext as Record<string, unknown>;

    const rows = Array.isArray(rawData)
        ? (rawData as Record<string, unknown>[])
        : [];

    const fp = rest.data_fingerprint as
        | { record_count?: number; symbols?: string[] }
        | undefined;
    const totalCount = fp?.record_count ?? rows.length;

    if (rows.length === 0) {
        return { ...rest };
    }

    const mentioned = extractMentionedSymbols(userMessage);
    let payloadRows: Record<string, unknown>[] = [];
    let truncated = false;

    if (mentioned.length > 0) {
        const matched = rows.filter((r) =>
            mentioned.some((sym) => rowMatchesSymbol(r, sym)),
        );
        payloadRows = matched.slice(0, CHAT_SYMBOL_FILTER_MAX_ROWS);
        truncated = matched.length > CHAT_SYMBOL_FILTER_MAX_ROWS;
    } else if (rows.length <= CHAT_PAYLOAD_MAX_ROWS) {
        payloadRows = rows;
    } else {
        payloadRows = rows.slice(0, CHAT_PAYLOAD_MAX_ROWS);
        truncated = true;
    }

    const symbols = Array.from(
        new Set(rows.map((r) => rowSymbol(r)).filter(Boolean)),
    );

    return {
        ...rest,
        rawData: payloadRows,
        raw_data_row_count: totalCount,
        raw_data_truncated: truncated,
        data_fingerprint: fp ?? {
            record_count: totalCount,
            symbols: symbols.slice(0, 30),
        },
    };
}
